import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSubscriptionTiers, useCurrentSubscription } from '@/hooks/useSubscription';
import { createCheckout } from '@/api/subscriptions';
import Button from '@/components/common/Button';
import Badge from '@/components/common/Badge';
import Spinner from '@/components/common/Spinner';

export default function PlanComparison() {
  const { data: tiersData, isLoading: tiersLoading } = useSubscriptionTiers();
  const { data: currentSub } = useCurrentSubscription();
  const queryClient = useQueryClient();
  const [changingTier, setChangingTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  if (tiersLoading) return <Spinner className="py-8" />;
  if (!tiersData) return null;

  const handleChangeTier = async (tierId: string) => {
    setError(null);
    setSuccessMessage(null);
    setChangingTier(tierId);

    try {
      const response = await createCheckout(tierId);

      if (response.action === 'checkout_created') {
        // New subscription — redirect to Stripe Checkout
        window.location.href = response.checkout_url;
      } else if (response.action === 'subscription_updated') {
        // Existing subscription was modified in-place
        setSuccessMessage(response.message);
        // Invalidate subscription query so the UI refreshes with new tier
        await queryClient.invalidateQueries({ queryKey: ['subscription', 'current'] });
      }
    } catch (err) {
      console.error('Failed to change subscription tier:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setChangingTier(null);
    }
  };

  // Order tiers for display
  const tierOrder = ['free', 'indie', 'pro'];
  const sortedTiers = [...tiersData.tiers].sort(
    (a, b) => tierOrder.indexOf(a.id) - tierOrder.indexOf(b.id),
  );

  const currentTierIndex = currentSub
    ? tierOrder.indexOf(currentSub.tier)
    : 0;

  return (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-neutral-900">Available Plans</h2>

      {successMessage && (
        <div className="mb-4 rounded-lg border border-success-200 bg-success-50 p-3 text-sm font-medium text-success-800">
          <div className="flex items-center justify-between">
            <p>{successMessage}</p>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-sm opacity-60 hover:opacity-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800">
          <div className="flex items-center justify-between">
            <p>{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-sm opacity-60 hover:opacity-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sortedTiers.map((tier) => {
          const isCurrent = currentSub?.tier === tier.id;
          const tierIndex = tierOrder.indexOf(tier.id);
          const isUpgrade = tierIndex > currentTierIndex;
          const isDowngrade = tierIndex < currentTierIndex;
          const isChanging = changingTier === tier.id;

          return (
            <div
              key={tier.id}
              className={`rounded-xl border p-5 ${
                isCurrent
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 bg-primary-10'
              }`}
            >
              <div className="mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-neutral-900">
                    {tier.name}
                  </h3>
                  {isCurrent && <Badge variant="primary">Current</Badge>}
                </div>
                <p className="mt-1 text-2xl font-bold text-neutral-900">
                  {tier.price_monthly === 0 ? 'Free' : `$${tier.price_monthly}/mo`}
                </p>
              </div>

              <ul className="mb-4 space-y-2 text-sm text-neutral-600">
                {tier.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="mt-0.5 text-primary-500">✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {isCurrent && (
                <Button variant="secondary" size="sm" className="w-full" disabled>
                  Current Plan
                </Button>
              )}
              {isUpgrade && tier.id !== 'free' && (
                <Button
                  variant="primary"
                  size="sm"
                  className="w-full"
                  onClick={() => handleChangeTier(tier.id)}
                  disabled={isChanging}
                >
                  {isChanging ? 'Processing...' : `Upgrade to ${tier.name}`}
                </Button>
              )}
              {isDowngrade && tier.id !== 'free' && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full"
                  onClick={() => handleChangeTier(tier.id)}
                  disabled={isChanging}
                >
                  {isChanging ? 'Processing...' : `Downgrade to ${tier.name}`}
                </Button>
              )}
              {isDowngrade && tier.id === 'free' && (
                <Button variant="secondary" size="sm" className="w-full" disabled>
                  Cancel via Billing
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
